import React, { useEffect, useMemo, useState } from 'react';
import { Copy, Trash2, Search, Pencil, FileText, File as FileIcon, Image as ImageIcon, Tag, X } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import UploadButton from './UploadButton';

const KIND_TABS = [
  { key: 'all', label: 'Tümü', icon: null },
  { key: 'image', label: 'Görseller', icon: ImageIcon },
  { key: 'pdf', label: "PDF'ler", icon: FileText },
  { key: 'doc', label: 'Belgeler', icon: FileIcon },
];

export default function AdminUploads() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [category, setCategory] = useState('all');

  const reload = () => api.adminListUploads().then(setItems).catch(() => setItems([]));
  useEffect(() => { reload(); }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach(it => { if (it.category) set.add(it.category); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [items]);

  const copy = (url) => {
    const full = url.startsWith('http') ? url : window.location.origin + url;
    navigator.clipboard.writeText(full); toast.success('URL kopyalandı');
  };

  const rename = async (it) => {
    const next = window.prompt('Yeni görünen ad:', it.display_name || it.name);
    if (next === null) return;
    const clean = next.trim();
    if (!clean) { toast.error('Boş ad geçersiz'); return; }
    try {
      await api.adminRenameUpload(it.name, clean);
      toast.success('Adı güncellendi');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Yeniden adlandırılamadı');
    }
  };

  const assignCategory = async (it) => {
    const suggestions = categories.length ? `\n\nMevcut kategoriler: ${categories.join(', ')}` : '';
    const next = window.prompt(`Kategori (boş bırakıp OK dersen kategori silinir):${suggestions}`, it.category || '');
    if (next === null) return;
    try {
      await api.adminUpdateUploadCategory(it.name, next.trim());
      toast.success(next.trim() ? 'Kategori atandı' : 'Kategori kaldırıldı');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const del = async (it) => {
    if (!window.confirm(`"${it.display_name || it.name}" dosyasını silmek istediğinize emin misiniz?`)) return;
    try {
      await api.adminDeleteUpload(it.name);
      toast.success('Silindi');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinemedi');
    }
  };

  const kindCounts = useMemo(() => {
    const c = { all: items.length, image: 0, pdf: 0, doc: 0 };
    items.forEach(it => { if (c[it.kind] !== undefined) c[it.kind] += 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let arr = items;
    if (kind !== 'all') arr = arr.filter(it => it.kind === kind);
    if (category !== 'all') {
      if (category === '__none__') arr = arr.filter(it => !it.category);
      else arr = arr.filter(it => it.category === category);
    }
    if (q.trim()) {
      const needle = q.toLowerCase();
      arr = arr.filter(it =>
        (it.display_name || '').toLowerCase().includes(needle) ||
        it.name.toLowerCase().includes(needle) ||
        (it.orig_name || '').toLowerCase().includes(needle) ||
        (it.category || '').toLowerCase().includes(needle)
      );
    }
    return arr;
  }, [items, q, kind, category]);

  const totalMb = items.reduce((s, x) => s + (x.size_mb || 0), 0);

  const renderPreview = (it) => {
    if (it.kind === 'image') {
      return <img src={it.url} alt={it.display_name || ''} className="w-full h-full object-cover" loading="lazy" />;
    }
    if (it.kind === 'pdf' && it.thumb_url) {
      return (
        <>
          <img src={it.thumb_url} alt={it.display_name || ''} className="w-full h-full object-cover" loading="lazy" />
          <span className="absolute bottom-2 left-2 text-[10px] tracking-wider font-bold bg-coral-300 text-white rounded px-1.5 py-0.5">PDF</span>
        </>
      );
    }
    const Icon = it.kind === 'pdf' ? FileText : FileIcon;
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-ink-50 to-ink-100 text-ink-500 gap-2">
        <Icon className="w-12 h-12" />
        <span className="text-[10px] uppercase tracking-wider font-bold">{it.kind === 'pdf' ? 'PDF' : 'BELGE'}</span>
      </div>
    );
  };

  const hasActiveFilters = kind !== 'all' || category !== 'all' || q.trim();

  return (
    <div data-testid="admin-uploads">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Görseller</div>
          <h1 className="font-display text-4xl mt-2 leading-none">Yüklenen Dosyalar</h1>
          <p className="mt-2 text-ink-400 text-sm">{items.length} dosya · {totalMb.toFixed(1)} MB toplam</p>
        </div>
        <UploadButton
          onUploaded={() => { reload(); }}
          label="Yeni Yükle"
          accept="image/*,application/pdf,.pdf,.doc,.docx"
          askName={true}
          askCategory={true}
          categorySuggestions={categories}
        />
      </div>

      {/* Kind filter tabs */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="kind-filter-tabs">
        {KIND_TABS.map(t => {
          const Icon = t.icon;
          const active = kind === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setKind(t.key)}
              data-testid={`kind-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-semibold transition-colors ${active ? 'bg-ink-600 text-cream' : 'bg-white border border-ink-100 text-ink-500 hover:border-ink-300'}`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {t.label}
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${active ? 'bg-cream/20' : 'bg-ink-50'}`}>{kindCounts[t.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Category filter chips */}
      {(categories.length > 0 || items.some(i => !i.category)) && (
        <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-ink-100" data-testid="category-filter-chips">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-ink-400 font-bold pr-1">
            <Tag className="w-3 h-3" /> Kategori
          </span>
          <button
            onClick={() => setCategory('all')}
            className={`text-xs h-7 px-3 rounded-full font-semibold transition-colors ${category === 'all' ? 'bg-gold text-ink-700' : 'bg-white border border-ink-100 text-ink-500 hover:border-gold'}`}
            data-testid="cat-chip-all"
          >
            Tümü
          </button>
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`text-xs h-7 px-3 rounded-full font-semibold transition-colors ${category === c ? 'bg-gold text-ink-700' : 'bg-white border border-ink-100 text-ink-500 hover:border-gold'}`}
              data-testid={`cat-chip-${c}`}
            >
              {c}
            </button>
          ))}
          {items.some(i => !i.category) && (
            <button
              onClick={() => setCategory('__none__')}
              className={`text-xs h-7 px-3 rounded-full font-semibold transition-colors italic ${category === '__none__' ? 'bg-gold text-ink-700' : 'bg-white border border-ink-100 text-ink-400 hover:border-gold'}`}
              data-testid="cat-chip-none"
            >
              Kategorisiz
            </button>
          )}
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="bg-white border border-ink-100 rounded-xl flex items-center gap-2 px-4 h-11 flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-ink-400 shrink-0" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ada, kategoriye veya dosyaya göre ara..." className="flex-1 bg-transparent outline-none text-sm" data-testid="uploads-search" />
          {q && <button onClick={() => setQ('')} className="text-xs text-ink-400 hover:text-ink-600">Temizle</button>}
        </div>
        {hasActiveFilters && (
          <button
            onClick={() => { setKind('all'); setCategory('all'); setQ(''); }}
            className="inline-flex items-center gap-1.5 text-xs h-9 px-3 rounded-lg bg-ink-50 hover:bg-ink-100 text-ink-500"
            data-testid="clear-filters"
          >
            <X className="w-3 h-3" /> Filtreleri temizle
          </button>
        )}
        <div className="text-xs text-ink-400 tabular">Gösterilen: <strong className="text-ink-600">{filtered.length}</strong> / {items.length}</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {filtered.map(it => (
          <div key={it.name} className="bg-white rounded-xl border border-ink-100 overflow-hidden hover:border-gold transition-colors group" data-testid={`upload-card-${it.name}`}>
            <div className="aspect-square bg-ink-50 overflow-hidden relative">
              {renderPreview(it)}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => assignCategory(it)} title="Kategori Ata" data-testid={`categorize-upload-${it.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-gold text-ink-700 hover:bg-gold/80">
                  <Tag className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => rename(it)} title="Yeniden adlandır" data-testid={`rename-upload-${it.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-ink-600 text-cream hover:bg-ink-500">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(it)} title="Sil" data-testid={`delete-upload-${it.name}`} className="w-8 h-8 grid place-items-center rounded-lg bg-coral-300 text-white hover:bg-coral-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {it.category && (
                <span className="absolute top-2 left-2 text-[10px] font-bold rounded-full bg-gold/95 text-ink-700 px-2 py-0.5 truncate max-w-[80%]" title={it.category}>
                  {it.category}
                </span>
              )}
            </div>
            <div className="p-2.5">
              <div className="text-[12px] truncate text-ink-600 font-semibold" title={it.display_name}>{it.display_name || it.name}</div>
              <div className="text-[10px] truncate text-ink-400" title={it.name}>{it.name}</div>
              <div className="text-[10px] text-ink-400 tabular mt-0.5">{it.size_mb}MB · {it.kind?.toUpperCase()}</div>
              <button onClick={() => copy(it.url)} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-ink-600 text-cream rounded-lg h-8 text-xs font-semibold hover:bg-ink-500" data-testid={`copy-url-${it.name}`}><Copy className="w-3 h-3" /> URL Kopyala</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl p-12 text-center text-ink-400 border border-ink-100">
            {hasActiveFilters ? 'Bu filtrelere uyan dosya yok' : 'Henüz yüklenmiş dosya yok'}
          </div>
        )}
      </div>
    </div>
  );
}
